/**
 * Windowing for the Machines grid (DesignSystem 4.4: lists virtualise above 100 rows).
 *
 * At 300 machines every card mounted was about 19,000 DOM nodes and 1.1 s of blocking work in
 * every 6 s of a live fleet, although `content-visibility` already skipped their paint. The
 * grid now lays its cards out in rows of the same column count as the CSS grid, all rows one
 * measured height, and mounts only the rows near the viewport.
 */
import { useEffect, useState } from 'react';

/** Tailwind's `gap-4`, between rows as between columns. */
export const GRID_GAP_PX = 16;
/** A card's height before the first one is measured. */
export const ESTIMATED_CARD_PX = 240;

/** The media queries behind `md:grid-cols-2 xl:grid-cols-3` (Tailwind v4 breakpoints). */
const MD = '(min-width: 48rem)';
const XL = '(min-width: 80rem)';

/** Columns for the viewport, as `grid-cols-1 md:grid-cols-2 xl:grid-cols-3` lays them out. */
export function columnsFor(matches: (query: string) => boolean): number {
  return matches(XL) ? 3 : matches(MD) ? 2 : 1;
}

/** The grid's column count, following the viewport. */
export function useGridColumns(): number {
  const matches = (q: string) => typeof window !== 'undefined' && !!window.matchMedia?.(q).matches;
  const [cols, setCols] = useState(() => columnsFor(matches));
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const lists = [window.matchMedia(MD), window.matchMedia(XL)];
    const onChange = () => setCols(columnsFor(matches));
    for (const l of lists) l.addEventListener('change', onChange);
    onChange();
    return () => {
      for (const l of lists) l.removeEventListener('change', onChange);
    };
  }, []);
  return cols;
}

/** Rows `[start, end)` of height `slot`, `gap` apart, that touch the band [top, bottom). */
export function rowWindow(rows: number, slot: number, gap: number, top: number, bottom: number): [number, number] {
  if (rows <= 0 || bottom <= top) return [0, 0];
  const pitch = Math.max(1, slot + gap);
  const start = Math.min(rows, Math.max(0, Math.floor(top / pitch)));
  const end = Math.min(rows, Math.max(start, Math.ceil(bottom / pitch)));
  return [start, end];
}

/**
 * The next row height, or null when the mounted rows already fit: exactly the tallest mounted
 * row the first time (replacing the estimate), and only ever larger after that, so the window
 * cannot flip between two heights (the Sessions list's React error 185).
 */
export function nextSlot(slot: number, tallest: number, measured: boolean): number | null {
  if (tallest <= 0) return null;
  const h = Math.ceil(tallest);
  if (!measured) return h === slot ? null : h;
  return h > slot ? h : null;
}
